'use client';

import { ActionButton } from '@/components/ui/ActionButton';
import { Text } from '@/components/ui/Text';
import { HelpBlock } from '../shared/HelpBlocks';
import {
  DiscountAppliedNotice,
  DiscountCodeError,
  DiscountCodeInput,
  MembershipIncludesBlock,
  MembershipMarketingBlock,
  QuoteActionButtons,
  QuoteMemberLineItem,
  QuoteTotal,
  TestimonialsSection,
  useQuoteData,
} from './quote';

function DiscountsBlock({
  discount,
  total,
}: {
  discount: number;
  total: number;
}) {
  return (
    <div className='flex flex-col gap-[8px] border-y-1 py-[16px]'>
      <div className='flex flex-row'>
        <Text variant='body' as='div'>
          Discount
        </Text>
        <Text variant='body' as='div' className='grow-1 text-right'>
          -${discount.toLocaleString()}
        </Text>
      </div>
      <div className='flex flex-row'>
        <Text variant='body' as='div' bold>
          Subtotal
        </Text>
        <Text variant='body' as='div' className='grow-1 text-right' bold>
          ${total.toLocaleString()}
        </Text>
      </div>
    </div>
  );
}

/**
 * Quote step - displays the personalized membership quote with per-member line items.
 */
export function QuoteStep() {
  const {
    members,
    totalPrice,
    discountedPrice,
    isPromotionUsed,
    discountPercentage,
    plans,
    isLoadingQuote,
    quoteError,
    isCheckingOut,
    discountCode,
    isApplyingDiscount,
    isApplyButtonEnabled,
    locationHasPediatrics,
    getMemberPrice,
    handleDiscountCodeChange,
    handleApplyDiscount,
    handleUpgradePlan,
    discountError,
    handleContinueToCheckout,
    handleEditQuote,
  } = useQuoteData();

  // Loading state
  if (isLoadingQuote) {
    return (
      <div className='mx-auto w-full max-w-[800px] px-4 py-8'>
        <div className='flex flex-col items-center gap-4'>
          <div className='h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-gray-600' />
          <p className='text-sh-gray-dark text-lg'>Calculating your quote...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (quoteError) {
    return (
      <div className='mx-auto w-full max-w-[800px] px-4 py-8'>
        <div className='flex flex-col items-center gap-4'>
          <div className='rounded-md bg-red-50 p-4 text-center'>
            <p className='text-red-600'>{quoteError}</p>
          </div>
          <ActionButton
            label='Try Again'
            variant='primary'
            type='button'
            onClick={() => window.location.reload()}
          />
        </div>
      </div>
    );
  }
  return (
    <>
      <div className='bg-sh-paper flex w-full justify-center'>
        <div className='w-full max-w-[1800px] rounded-4xl bg-white px-[16px] py-8 md:mx-[60px] md:my-[48px] md:px-[200px]'>
          <div className='mb-[40px] flex flex-col gap-[40px]'>
            <div className='text-center'>
              <Text variant='h1-serif' color='dark'>
                Your Personalized Membership Quote
              </Text>
              <Text variant='body' as='p' color='dark' className='mt-[40px]'>
                Rates vary by age, family size, and membership tier.
              </Text>
            </div>

            {/* Member line items */}
            <div className='space-y-0'>
              {members.map((member, index) => (
                <QuoteMemberLineItem
                  key={index}
                  member={member}
                  price={getMemberPrice(member.first_name, member.last_name)}
                />
              ))}
            </div>

            <QuoteTotal
              totalPrice={totalPrice}
              isPromotionUsed={isPromotionUsed}
            />

            <DiscountCodeInput
              discountCode={discountCode}
              onChange={handleDiscountCodeChange}
              onApply={handleApplyDiscount}
              isApplying={isApplyingDiscount}
              isEnabled={isApplyButtonEnabled}
            />

            {discountError && <DiscountCodeError message={discountError} />}

            {isPromotionUsed && (
              <>
                <DiscountAppliedNotice percentage={discountPercentage} />
                <DiscountsBlock
                  discount={totalPrice - discountedPrice}
                  total={discountedPrice}
                />
              </>
            )}

            <QuoteActionButtons
              onCheckout={handleContinueToCheckout}
              onEdit={handleEditQuote}
              isCheckingOut={isCheckingOut}
              isLoadingQuote={isLoadingQuote}
            />
          </div>
          <h3 className='mb-[40px] text-center font-serif text-[36px] leading-[130%] font-light text-[#292722]'>
            Included in Your Membership
          </h3>
          <div className='mb-[44px] py-[16px]'>
            <MembershipIncludesBlock plans={plans} />
          </div>
        </div>
      </div>

      <div className='bg-sh-paper flex w-full justify-center'>
        <div className='max-w-[1800px]'>
          <MembershipMarketingBlock
            plans={plans}
            pediatrics={locationHasPediatrics}
            callback={handleUpgradePlan}
          />
        </div>
      </div>

      <div className='bg-sh-cream flex w-full justify-center px-[21px]'>
        <HelpBlock />
      </div>

      <TestimonialsSection />

      <QuoteActionButtons
        onCheckout={handleContinueToCheckout}
        onEdit={handleEditQuote}
        isCheckingOut={isCheckingOut}
        isLoadingQuote={isLoadingQuote}
        showBackToTop
      />
    </>
  );
}
